// screen: Contribution sheet (change c14-clean-feed F7) — every push feed of every channel on one printable page for the
// broadcaster's engineer: host, port, stream id, allowed encoder addresses, the recommended encoder settings per ladder
// and ready FFmpeg SRT caller lines. Engineer+ (channels:write); passphrases only through "Show secrets". Bilingual
// labels (the broadcast engineer may read English); commands and addresses are LTR.
import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import type { ContributionRec, IngestFeedKey } from '@viewstream/api-client';
import { Button } from '@viewstream/ui';
import { useTenant } from '~/app/tenant';
import { can } from '~/app/permissions';
import { useChannels, type Channel } from '~/app/live';
import { useIngest } from '~/app/ingest';
import { RevealSecrets } from '~/app/IngestSettings';

export const Route = createFileRoute('/_layout/t/$slug/contribution')({ component: Screen });

/** "עברית / English" — the sheet is read by the studio's broadcast engineer too. */
function Bi({ he, en }: { he: string; en: string }) {
  return <>{he} <span className="text-fg-muted" dir="ltr" lang="en">/ {en}</span></>;
}

function Screen() {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  const chans = useChannels(tenant.slug);
  if (!can(scopes, 'channels:write')) return <p className="p-6 text-sm">{t`Only Engineers and above can see the contribution sheet.`}</p>;
  return (
    <div className="space-y-6 p-6 print:p-0" data-testid="contribution-sheet">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold"><Bi he="דף שידור נכנס" en="Contribution sheet" /></h1>
        <Button variant="ghost" onClick={() => window.print()} className="print:hidden">{t`Print`}</Button>
      </div>
      <p className="max-w-3xl text-sm text-fg-muted">{t`Every push feed of every channel, for the engineer who configures the contribution encoders. Passphrases appear only after "Show secrets". Each feed accepts only the encoder addresses listed under it.`}</p>
      {(chans.data ?? []).map((c) => <ChannelBlock key={c.id} ch={c} />)}
    </div>
  );
}

function recText(rec: ContributionRec | undefined): string {
  if (!rec) return '';
  const fps = rec.fps === 'source' ? 'source rate' : `${rec.resolution}${rec.fps}`.replace('pp', 'p');
  return rec.mode === 'passthrough'
    ? `${fps} progressive, ${rec.codec} High, ${rec.min_mbps}-${rec.max_mbps} Mbps capped VBR, GOP ${rec.gop_s ?? 2} s fixed (IDR on even wall-clock seconds), ${rec.audio} 128 kbps, ${rec.protocol}`
    : `${fps} progressive, ${rec.codec} High, ${rec.min_mbps}-${rec.max_mbps} Mbps capped VBR, GOP 2 s, ${rec.audio} 128 kbps, ${rec.protocol}`;
}

function ffmpegLine(host: string, port: number, streamId: string | undefined, rec: ContributionRec | undefined): string {
  const mbps = rec ? rec.max_mbps : 15;
  const fps = rec?.fps === '25' ? 25 : 50;
  const q = new URLSearchParams({ mode: 'caller', latency: '500000', pbkeylen: '32', passphrase: '<PASSPHRASE>' });
  if (streamId) q.set('streamid', streamId);
  return `ffmpeg -f decklink -i 'DeckLink Duo (1)' -vf bwdif=mode=send_frame -c:v libx264 -preset veryfast -profile:v high -b:v ${mbps}M -maxrate ${mbps}M -bufsize ${mbps * 2}M -g ${fps * 2} -keyint_min ${fps * 2} -sc_threshold 0 -force_key_frames 'expr:eq(mod(n,${fps * 2}),0)' -pix_fmt yuv420p -c:a aac -b:a 128k -ar 48000 -f mpegts 'srt://${host}:${port}?${q.toString()}'`;
}

function ChannelBlock({ ch }: { ch: Channel }) {
  const { t } = useLingui();
  const { tenant } = useTenant();
  const ing = useIngest(tenant.slug, ch.id!);
  const rec = (ch as Channel & { contribution?: ContributionRec }).contribution;
  const internal = (ch as Channel & { visibility?: string }).visibility === 'internal';
  const eps = ing.data?.endpoints ?? {};
  const feeds = (Object.keys(eps) as IngestFeedKey[]).filter((f) => eps[f]);
  return (
    <section className="max-w-4xl break-inside-avoid space-y-2 rounded-lg border border-border p-4" data-testid={`contrib-${ch.slug}`}>
      <h2 className="text-lg font-semibold">{ch.title ?? ch.slug} <code className="text-sm text-fg-muted ltr-isolate" dir="ltr">{ch.slug}</code>{internal ? <span className="ms-2 text-xs text-fg-muted"><Bi he="פנימי" en="internal (clean feed)" /></span> : null}</h2>
      {rec ? <p className="text-sm"><Bi he="הגדרות מקודד מומלצות" en="Recommended encoder settings" />: <code className="ltr-isolate" dir="ltr">{recText(rec)}</code></p> : null}
      {feeds.length === 0 ? <p className="text-sm text-fg-muted">{t`No push feed on this channel (it pulls its source, or ingest is not set up).`}</p> : null}
      {feeds.map((f) => {
        const ep = eps[f]!;
        const ips = (ing.data?.allowed_ips ?? []).filter((r) => r.feed === f && r.state === 'active');
        return (
          <div key={f} className="space-y-1 rounded-md bg-surface-2 p-3 text-sm" data-testid={`contrib-${ch.slug}-${f}`}>
            <div className="font-medium"><Bi he={f === 'a' ? 'פיד A (ראשי)' : 'פיד B (גיבוי)'} en={f === 'a' ? 'Feed A (primary)' : 'Feed B (backup)'} /></div>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5">
              <dt className="text-fg-muted"><Bi he="כתובת" en="Host" /></dt><dd><code className="ltr-isolate" dir="ltr">{ep.host}</code></dd>
              <dt className="text-fg-muted"><Bi he="פורט" en="Port" /></dt><dd><code className="ltr-isolate" dir="ltr">{ep.port}/{ep.proto}</code></dd>
              {ep.stream_id ? <><dt className="text-fg-muted"><Bi he="מזהה זרם" en="Stream ID" /></dt><dd><code className="ltr-isolate" dir="ltr">{ep.stream_id}</code></dd></> : null}
              <dt className="text-fg-muted"><Bi he="כתובות מקודד מורשות" en="Allowed encoder IPs" /></dt>
              <dd>{ips.length ? ips.map((r) => <code key={r.id} className="me-2 ltr-isolate" dir="ltr">{r.cidr}</code>) : <span className="text-warn">{t`none yet — the port stays closed`}</span>}</dd>
            </dl>
            {ep.mode === 'push_srt' ? <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded bg-surface p-2 text-xs" dir="ltr">{ffmpegLine(ep.host, ep.port, ep.stream_id, rec)}</pre> : null}
            <div className="print:hidden"><RevealSecrets channelId={ch.id!} feed={f} /></div>
          </div>
        );
      })}
    </section>
  );
}
